import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X, PenLine } from "lucide-react";
import { useCart } from "@/context/CartContext";
import InquiryDrawer from "@/components/InquiryDrawer";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { name: "Original Works", path: "/portfolio", id: "nav-link-portfolio" },
  { name: "Notecards", path: "/notecards", id: "nav-link-notecards" },
  { name: "About the Studio", path: "/about", id: "nav-link-about" },
  { name: "Contact", path: "/contact", id: "nav-link-contact" },
];

export default function Navbar() {
  const { count, setDrawerOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquireOpen, setInquireOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-paper/85 border-b border-ink/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-10 h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-brand-logo" className="leading-none shrink-0" onClick={() => setMobileOpen(false)}>
            <span className="font-serif text-xl sm:text-2xl tracking-tight text-ink">Artist&rsquo;s Studio Gallery</span>
            <span className="block font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-stone2 mt-1">
              Richard Kuensting
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors duration-300 hover:text-terracotta ${isActive ? "text-terracotta" : "text-walnut"}`
                }
              >
                {l.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="nav-inquire-btn"
              onClick={() => setInquireOpen(true)}
              className="hidden md:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border border-ink/20 rounded-full px-4 py-2 hover:bg-ink hover:text-linen transition-colors duration-300"
            >
              <PenLine size={13} /> Inquire
            </button>
            <button
              data-testid="nav-cart-btn"
              onClick={() => setDrawerOpen(true)}
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-ink/20 hover:bg-ink hover:text-linen transition-colors duration-300"
              aria-label="Open cart"
            >
              <ShoppingBag size={17} />
              {count > 0 && (
                <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-terracotta text-linen text-[10px] font-mono w-5 h-5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="nav-mobile-menu-btn"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-ink/20"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-linen pt-24 px-8 lg:hidden"
          >
            <div className="flex flex-col gap-2">
              <Link to="/" data-testid="nav-link-home-mobile" onClick={() => setMobileOpen(false)}
                className="font-serif text-4xl py-3 border-b border-ink/10 text-ink">
                Home
              </Link>
              {LINKS.map((l, i) => (
                <motion.div key={l.path} initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.5, ease: EASE }}>
                  <NavLink to={l.path} data-testid={`${l.id}-mobile`} onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block font-serif text-4xl py-3 border-b border-ink/10 ${isActive ? "text-terracotta italic" : "text-ink"}`
                    }>
                    {l.name}
                  </NavLink>
                </motion.div>
              ))}
              <button
                data-testid="nav-inquire-btn-mobile"
                onClick={() => { setMobileOpen(false); setInquireOpen(true); }}
                className="mt-8 self-start inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] bg-ink text-linen rounded-full px-6 py-3"
              >
                <PenLine size={13} /> Inquire with the Studio
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <InquiryDrawer open={inquireOpen} onClose={() => setInquireOpen(false)} />
    </>
  );
}
